/**
 * Mermaid C4 Diagram Generator
 *
 * Generates Mermaid flowchart diagrams from TemplateEntry service specs.
 * Uses a C4-inspired container layout with network boundaries, service types,
 * and data flow arrows. Designed to render in both light and dark themes.
 */

import type { TemplateEntry, ServiceSpec } from "../data/template-registry";

const SERVICE_TYPE_STYLES: Record<string, { fill: string; stroke: string }> = {
  static: { fill: "#2563eb", stroke: "#2563eb" },
  server: { fill: "#2563eb", stroke: "#2563eb" },
  stateful: { fill: "#7c3aed", stroke: "#7c3aed" },
  scheduler: { fill: "#06b6d4", stroke: "#06b6d4" },
  cronjob: { fill: "#f59e0b", stroke: "#f59e0b" },
};

const SERVICE_TYPE_LABELS: Record<string, string> = {
  static: "Static",
  server: "Server",
  stateful: "Stateful",
  scheduler: "Scheduler",
  cronjob: "CronJob",
};

function sanitizeNodeId(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, "_");
}

function formatServiceLabel(service: ServiceSpec): string {
  const parts = [service.name];
  if (service.image) {
    const imgTag = service.image.split(":")[0].split("/").pop();
    if (imgTag) parts.push(`<br/>${imgTag}`);
  } else if (service.vcs?.baseImage) {
    const baseTag = service.vcs.baseImage.split(":")[0];
    parts.push(`<br/>${baseTag}`);
  }
  const typeLabel = SERVICE_TYPE_LABELS[service.type] || service.type;
  parts.push(`<br/>(${typeLabel})`);
  if (service.port) parts.push(`:${service.port}`);
  return parts.join("");
}

/**
 * Generates a Mermaid flowchart diagram from a template's service definitions.
 *
 * Layout strategy:
 * - Client node on the left
 * - Internet-facing services in a "Public" subgraph
 * - Internal services in a "Private" subgraph
 * - Stateful services (databases, caches) in a "Data" subgraph
 * - Arrows show data flow from client -> public -> private -> data
 */
export function generateMermaidDiagram(template: TemplateEntry): string {
  const { services } = template;
  if (!services || services.length === 0) {
    return `flowchart TB\n  Empty["No services defined"]`;
  }

  const publicServices = services.filter((s) => s.internetFacing);
  const privateServices = services.filter(
    (s) => !s.internetFacing && s.type !== "stateful",
  );
  const dataServices = services.filter(
    (s) => !s.internetFacing && s.type === "stateful",
  );

  const lines: string[] = ["flowchart LR"];

  // Client node
  lines.push('  Client["Client<br/>Requests"]');

  // Public subgraph (internet-facing services)
  if (publicServices.length > 0) {
    lines.push("  subgraph Public[" + "Public" + "]");
    for (const svc of publicServices) {
      const id = sanitizeNodeId(svc.name);
      lines.push(`    ${id}["${formatServiceLabel(svc)}"]`);
    }
    lines.push("  end");
  }

  // Private subgraph (internal servers/schedulers)
  if (privateServices.length > 0) {
    lines.push("  subgraph Private[" + "Private" + "]");
    for (const svc of privateServices) {
      const id = sanitizeNodeId(svc.name);
      lines.push(`    ${id}["${formatServiceLabel(svc)}"]`);
    }
    lines.push("  end");
  }

  // Data subgraph (databases, caches)
  if (dataServices.length > 0) {
    lines.push("  subgraph Data[" + "Data Layer" + "]");
    for (const svc of dataServices) {
      const id = sanitizeNodeId(svc.name);
      lines.push(`    ${id}[("${formatServiceLabel(svc)}")]`);
    }
    lines.push("  end");
  }

  // Connections
  // Client -> public services
  for (const svc of publicServices) {
    const id = sanitizeNodeId(svc.name);
    lines.push(`  Client --> ${id}`);
  }

  // Public -> private services
  if (publicServices.length > 0 && privateServices.length > 0) {
    const firstPublic = sanitizeNodeId(publicServices[0].name);
    for (const svc of privateServices) {
      const id = sanitizeNodeId(svc.name);
      lines.push(`  ${firstPublic} --> ${id}`);
    }
  }

  // Public/private -> data services
  const allCompute = [...publicServices, ...privateServices];
  for (const svc of dataServices) {
    const dataId = sanitizeNodeId(svc.name);
    // Connect to the first server-type service, or first public service
    const serverService = allCompute.find((s) => s.type === "server");
    if (serverService) {
      const sourceId = sanitizeNodeId(serverService.name);
      lines.push(`  ${sourceId} --> ${dataId}`);
    } else if (allCompute.length > 0) {
      const sourceId = sanitizeNodeId(allCompute[0].name);
      lines.push(`  ${sourceId} --> ${dataId}`);
    }
  }

  // Styles
  lines.push("  style Client fill:#374151,stroke:#374171,color:#fff");

  for (const svc of [...publicServices, ...privateServices, ...dataServices]) {
    const id = sanitizeNodeId(svc.name);
    const style = SERVICE_TYPE_STYLES[svc.type] || SERVICE_TYPE_STYLES.server;
    const textColor = svc.type === "stateful" ? "#fff" : "#fff";
    lines.push(
      `  style ${id} fill:${style.fill},stroke:${style.stroke},color:${textColor}`,
    );
  }

  // Subgraph styles
  if (publicServices.length > 0) {
    lines.push("  style Public fill:#dbeafe,stroke:#2563eb,stroke-width:1px");
  }
  if (privateServices.length > 0) {
    lines.push("  style Private fill:#fef3c7,stroke:#f59e0b,stroke-width:1px");
  }
  if (dataServices.length > 0) {
    lines.push("  style Data fill:#ede9fe,stroke:#7c3aed,stroke-width:1px");
  }

  return lines.join("\n");
}
